import * as angular from 'angular';
import * as _ from 'lodash';
import FormulaTester, { openFormulaTester } from "./formulaTester.controller";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { Plant } from "../../models/navigation";
import { EnumOption, UIUtilityService } from "../../services/utility/uiUtility.service";
import DataPointServiceName, { DataPointService } from "../../services/dataPoints/dataPoints.service";
import NotifyServiceName, { NotifyService } from "../../services/notify/notify.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import NewGuidServiceName, { NewGuidService } from "../../services/utility/newguid.service";
import { apsCommands } from "../../shared/dto/aps.commands";
import { PlantLocation } from "../../services/locations/locations.service";
import { apsEnums } from "../../shared/dto/aps.enums";
import template from './editFormula.tpl.html';
import AddUpdateFormulaVariable = apsCommands.AddUpdateFormulaVariable;

let requires = [FormulaTester, DataPointServiceName, NotifyServiceName, LoaderServiceName, NewGuidServiceName];

export interface EditFormulaControllerParams {
  instance: apsViewModels.DataPointDetail;
  plant: Plant;
  allDataPoints: apsViewModels.DataPointDetail[];
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
}

export interface FormulaDetail extends apsViewModels.DataPointDetail {
  location: string;
  typeName: string;
}

export function openEditFormula(
  data: EditFormulaControllerParams,
  $modal: mm.foundation.modal): angular.IPromise<FormulaDetail> {
  return $modal.open({
    template: template,
    controller: 'EditFormulaCtrl as vm',
    resolve: {
      data: () => data
    }
  }).result;
}

interface EditFormulaController {
  getDataPoints(variable: apsViewModels.FormulaVariableDetail): apsViewModels.DataPointDetail[];
  types: EnumOption[];
  variableTypes: EnumOption[];
  allVariableTypes: EnumOption[];
  form: angular.IFormController;
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
  shops: PlantLocation[];
  addVariable(): void;
  removeVariable(variable: apsViewModels.FormulaVariableDetail): void;
  save(): void;
  cancel(): void;
  testFormula(): void;
  errors: string[];
  clearErrors(): void;
  locationChanged(): void;
  detailDataPointChanged(variable: FormulaVariableDetail): void;
}

interface FormulaVariableDetail extends apsViewModels.FormulaVariableDetail {
  dataPointName: string;
}

class EditFormulaCtrl implements EditFormulaController {
  types: EnumOption[];
  variableTypes: EnumOption[];
  allVariableTypes: EnumOption[];
  form: angular.IFormController;
  plant: Plant;
  errors: string[];
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
  shops: PlantLocation[];

  private dataPointService: DataPointService;
  private $modalInstance: mm.foundation.modalInstance;
  private notifyService: NotifyService;
  private loaderService: LoaderService;
  private newGuidService: NewGuidService;
  private dataPoint: apsCommands.StandaloneDataPoint;
  private allDataPoints: apsViewModels.DataPointDetail[];
  private refDataPoints: apsViewModels.DataPointDetail[];
  private $modal: mm.foundation.modal;
  private add: boolean;

  constructor(
    data: EditFormulaControllerParams,
    newGuidService: NewGuidService,
    dataPointService: DataPointService,
    $modalInstance: mm.foundation.modalInstance,
    $modal: mm.foundation.modal,
    notifyService: NotifyService,
    loaderService: LoaderService,
    uiUtilityService: UIUtilityService
  ) {
    this.dataPointService = dataPointService;
    this.$modalInstance = $modalInstance;
    this.notifyService = notifyService;
    this.loaderService = loaderService;
    this.newGuidService = newGuidService;
    this.$modal = $modal;

    this.errors = [];
    this.plant = data.plant;
    this.allDataPoints = data.allDataPoints;
    this.locations = data.locations;
    this.selectedLocation = data.selectedLocation;
    if (data.instance) {
      this.selectedLocation = _.find(this.locations, l => l.location.id === data.instance.plantLocationId);
    }
    if (!this.selectedLocation) {
      this.selectedLocation = data.locations[0];
    }

    if (data.instance) {
      this.dataPoint = JSON.parse(JSON.stringify(data.instance));
      this.dataPoint.variables.forEach((v: FormulaVariableDetail) => {
        if (v.referenceDataPointId) {
          let dataPoint = _.find(this.allDataPoints, { id: v.referenceDataPointId });
          if (dataPoint) {
            v.dataPointName = dataPoint.name;
          }
        }
      });
      this.add = false;
    } else {
      this.dataPoint = {
        configType: apsEnums.DataPointConfigType.Formula,
        variables: [],
        plantId: this.plant.id,
        id: null,
        config: '',
        name: '',
        type: null,
        plantLocationId: null
      };
      newGuidService.getNewGuid('dataPoint').then(id => this.dataPoint.id = id);
      this.add = true;
    }

    this.types = uiUtilityService.getListOptionsFromEnum(apsEnums.DataPointType, 1);
    this.allVariableTypes = uiUtilityService.getListOptionsFromEnum(apsEnums.VariableType, 1);
    this.locationChanged();
  }

  locationChanged(): void {
    this.dataPoint.plantLocationId = this.selectedLocation.location.id;
    let currentLocation = this.selectedLocation;
    let locationIds = [];
    while (currentLocation) {
      locationIds.push(currentLocation.location.id);
      currentLocation = currentLocation.parent;
    }

    this.shops = [this.selectedLocation]
      .concat(this.selectedLocation.children)
      .filter(l => l.locationType === apsEnums.LocationType.Shop);

    if (this.shops.length === 0) {
      this.variableTypes = this.allVariableTypes.filter(v => v.value === apsEnums.VariableType.DataPoint);
      this.dataPoint.variables.filter(x => x.type !== apsEnums.VariableType.DataPoint)
        .forEach(x => x.type = apsEnums.VariableType.DataPoint);
    } else {
      this.variableTypes = this.allVariableTypes;
      if (this.shops.length === 1) {
        this.dataPoint.variables.filter(x => x.type !== apsEnums.VariableType.DataPoint)
          .forEach(x => x.shopId = this.shops[0].id);
      }
    }

    this.refDataPoints = _.filter(this.allDataPoints, dp => dp.id !== this.dataPoint.id
      && _.some(locationIds, id => dp.plantLocationId === id));
  }

  getDataPoints(variable: FormulaVariableDetail): apsViewModels.DataPointDetail[] {
    let refIds = _.map<AddUpdateFormulaVariable, string>(_.filter(this.dataPoint.variables,
      v => v !== variable && !!v.referenceDataPointId), 'referenceDataPointId');
    return _.filter(this.refDataPoints, dp => _.every(refIds, x => x !== dp.id));
  }

  detailDataPointChanged(variable: FormulaVariableDetail) {
    let dataPoint = _.find(this.refDataPoints, { name: variable.dataPointName });
    if (dataPoint) {
      variable.referenceDataPointId = dataPoint.id;
      return;
    }

    variable.dataPointName = '';
    variable.referenceDataPointId = null;
  }

  addVariable(): void {
    let variable: apsCommands.AddUpdateFormulaVariable = {
      id: null,
      type: null,
      name: '',
      referenceDataPointId: null,
      shopId: null
    };
    this.newGuidService.getNewGuid('formulaVariable').then(id => variable.id = id);
    this.dataPoint.variables.push(variable);
  }

  removeVariable(variable: apsViewModels.FormulaVariableDetail): void {
    _.remove(this.dataPoint.variables, variable);
  }

  clearErrors(): void {
    this.errors = [];
  }

  save(): void {
    if (this.form.$invalid) {
      return;
    }

    this.errors = [];
    this.dataPoint.variables.forEach(v => {
      if (v.type === apsEnums.VariableType.DataPoint) {
        v.shopId = null;
      } else {
        v.referenceDataPointId = null;
      }
    });

    let promise = this.dataPointService.validateFormula({
      formula: this.dataPoint.config,
      variableNames: _.map(this.dataPoint.variables, 'name')
    }).then(errors => {
      if (errors.length > 0) {
        this.errors = errors;
        return;
      }

      let method = this.add ? this.dataPointService.add : this.dataPointService.update;
      return method(this.dataPoint)
        .then(() => {
          this.$modalInstance.close(this.dataPoint);
        }, this.notifyService.handleApiErrors);
    }, this.notifyService.handleApiErrors);

    this.loaderService.loadingPromise(promise);
  }

  testFormula(): void {
    openFormulaTester({
      formula: this.dataPoint.config,
      variables: _.map(this.dataPoint.variables, 'name')
    }, this.$modal);
  }

  cancel(): void {
    this.$modalInstance.close();
  }
}

export default angular.module('aps.controller.editFormula', requires)
  .controller('EditFormulaCtrl', EditFormulaCtrl)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/formulas/editFormula.controller.ts